import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  LogOut, Download, Users, Sparkles, PhoneCall, CheckCircle2,
  Eye, FileText, Loader2, Filter, RotateCcw,
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "../components/ui/table";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "../components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "../components/ui/dialog";
import { LEAD_STATUSES, FACILITY_OPTIONS, FUNDING_OPTIONS } from "../constants/options";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_COLORS = {
  New: "bg-globus-tealLight text-globus-tealDark",
  Contacted: "bg-globus-navy/10 text-globus-navy",
  "In Progress": "bg-slate-200 text-globus-navy",
  Closed: "bg-slate-100 text-slate-500",
};

export default function AdminDashboard() {
  const [leads, setLeads] = useState(null);
  const [selected, setSelected] = useState(null);
  const [filters, setFilters] = useState({ date_from: "", date_to: "", facility: "", funding_amount: "", sort: "date" });
  const navigate = useNavigate();
  const token = localStorage.getItem("globus_admin_token");
  const headers = { Authorization: `Bearer ${token}` };

  const filterParams = (f) => {
    const params = new URLSearchParams();
    Object.entries(f).forEach(([k, v]) => { if (v) params.append(k, v); });
    return params.toString();
  };

  const load = useCallback(async (f) => {
    try {
      const qs = filterParams(f ?? filters);
      const { data } = await axios.get(`${API}/admin/leads${qs ? `?${qs}` : ""}`, { headers });
      setLeads(data.leads);
    } catch (err) {
      if (err.response?.status === 401) {
        localStorage.removeItem("globus_admin_token");
        navigate("/admin/login");
      } else {
        toast.error("Failed to load leads.");
      }
    }
  }, [navigate, filters]);

  useEffect(() => {
    if (!token) { navigate("/admin/login"); return; }
    load();
  }, [token, navigate, load]);

  const applyFilter = (key, value) => {
    const next = { ...filters, [key]: value };
    setFilters(next);
    load(next);
  };

  const resetFilters = () => {
    const empty = { date_from: "", date_to: "", facility: "", funding_amount: "", sort: "date" };
    setFilters(empty);
    load(empty);
  };

  const hasFilters = Object.entries(filters).some(([k, v]) => k !== "sort" && Boolean(v));

  const GRADE_STYLES = {
    Strong: "bg-globus-tealLight text-globus-tealDark",
    Moderate: "bg-globus-navy/10 text-globus-navy",
    "Needs Review": "bg-slate-100 text-slate-500",
  };

  const updateStatus = async (leadId, status) => {
    try {
      await axios.patch(`${API}/admin/leads/${leadId}/status`, { status }, { headers });
      setLeads((ls) => ls.map((l) => (l.id === leadId ? { ...l, status } : l)));
      toast.success(`Status updated to ${status}`);
    } catch {
      toast.error("Failed to update status.");
    }
  };

  const downloadCsv = async () => {
    try {
      const qs = filterParams(filters);
      const res = await axios.get(`${API}/admin/leads-export/csv${qs ? `?${qs}` : ""}`, { headers, responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `globus-leads-${new Date().toISOString().slice(0, 10)}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("CSV download failed.");
    }
  };

  const openFile = async (lead) => {
    try {
      const res = await axios.get(`${API}/files/${lead.file.storage_path}`, { headers, responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      window.open(url, "_blank");
    } catch {
      toast.error("Could not open the file.");
    }
  };

  const logout = () => {
    localStorage.removeItem("globus_admin_token");
    navigate("/admin/login");
  };

  const count = (s) => (leads || []).filter((l) => l.status === s).length;

  const stats = [
    { label: "Total Leads", value: leads?.length ?? "-", icon: Users },
    { label: "New", value: leads ? count("New") : "-", icon: Sparkles },
    { label: "Contacted", value: leads ? count("Contacted") : "-", icon: PhoneCall },
    { label: "Closed", value: leads ? count("Closed") : "-", icon: CheckCircle2 },
  ];

  return (
    <div data-testid="admin-dashboard" className="min-h-screen bg-[#F1F5F9]">
      <header className="sticky top-0 z-40 border-b border-slate-200/50 bg-white/70 backdrop-blur-xl">
        <div className="mx-auto flex max-w-7xl items-center justify-between px-4 py-3 md:px-6">
          <div className="flex items-center" data-testid="admin-brand">
            <img src="/globus-logo.png" alt="Globus Financial Services" data-testid="admin-logo-img" className="h-14 w-auto" />
          </div>
          <div className="flex items-center gap-2">
            <Button data-testid="download-csv-button" onClick={downloadCsv} variant="outline" size="sm"
              className="rounded-md border-globus-teal text-globus-teal hover:bg-globus-tealLight hover:text-globus-tealDark transition-colors duration-200">
              <Download className="mr-2 h-4 w-4" /> CSV
            </Button>
            <Button data-testid="admin-logout-button" onClick={logout} variant="ghost" size="sm"
              className="rounded-md text-slate-600 hover:text-globus-navy hover:bg-slate-100 transition-colors duration-200">
              <LogOut className="mr-2 h-4 w-4" /> Logout
            </Button>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-7xl p-4 md:p-6">
        <div className="grid grid-cols-2 gap-4 md:grid-cols-4" data-testid="admin-stats">
          {stats.map((s) => (
            <div key={s.label} className="rounded-md border border-slate-200 bg-white p-4 md:p-5 transition-all duration-200 hover:-translate-y-1 hover:shadow-sm">
              <div className="flex items-center justify-between">
                <p className="text-xs tracking-[0.15em] uppercase font-semibold text-slate-500">{s.label}</p>
                <s.icon className="h-4 w-4 text-globus-teal" />
              </div>
              <p className="font-heading mt-2 text-3xl text-globus-navy" data-testid={`stat-${s.label.toLowerCase().replace(/\s/g, "-")}`}>{s.value}</p>
            </div>
          ))}
        </div>

        <div className="mt-6 rounded-md border border-slate-200 bg-white" data-testid="leads-table-card">
          <div className="border-b border-slate-200 p-4" data-testid="lead-filters">
            <div className="flex items-center gap-2 mb-3">
              <Filter className="h-4 w-4 text-globus-teal" />
              <p className="text-xs tracking-[0.15em] uppercase font-semibold text-slate-500">Filter leads</p>
              {hasFilters && <span className="rounded-full bg-globus-tealLight px-2 py-0.5 text-[10px] font-semibold text-globus-tealDark" data-testid="filters-active-badge">Active</span>}
            </div>
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-6">
              <div>
                <Label htmlFor="filter-date-from" className="text-xs text-slate-500">From date</Label>
                <Input data-testid="filter-date-from" id="filter-date-from" type="date" value={filters.date_from}
                  onChange={(e) => applyFilter("date_from", e.target.value)} className="mt-1 h-9 text-sm" />
              </div>
              <div>
                <Label htmlFor="filter-date-to" className="text-xs text-slate-500">To date</Label>
                <Input data-testid="filter-date-to" id="filter-date-to" type="date" value={filters.date_to}
                  onChange={(e) => applyFilter("date_to", e.target.value)} className="mt-1 h-9 text-sm" />
              </div>
              <div>
                <Label className="text-xs text-slate-500">Facility</Label>
                <Select value={filters.facility} onValueChange={(v) => applyFilter("facility", v === "__all__" ? "" : v)}>
                  <SelectTrigger data-testid="filter-facility" className="mt-1 h-9 text-sm"><SelectValue placeholder="All facilities" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__all__" data-testid="filter-facility-all">All facilities</SelectItem>
                    {FACILITY_OPTIONS.map((o) => <SelectItem key={o} value={o} data-testid={`filter-facility-${o}`}>{o}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs text-slate-500">Funding amount</Label>
                <Select value={filters.funding_amount} onValueChange={(v) => applyFilter("funding_amount", v === "__all__" ? "" : v)}>
                  <SelectTrigger data-testid="filter-funding" className="mt-1 h-9 text-sm"><SelectValue placeholder="All amounts" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__all__" data-testid="filter-funding-all">All amounts</SelectItem>
                    {FUNDING_OPTIONS.map((o) => <SelectItem key={o} value={o} data-testid={`filter-funding-${o}`}>{o}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs text-slate-500">Sort by</Label>
                <Select value={filters.sort} onValueChange={(v) => applyFilter("sort", v)}>
                  <SelectTrigger data-testid="filter-sort" className="mt-1 h-9 text-sm"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="date" data-testid="filter-sort-date">Newest first</SelectItem>
                    <SelectItem value="score" data-testid="filter-sort-score">Strongest prospects first</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="flex items-end">
                <Button data-testid="filter-reset-button" onClick={resetFilters} variant="ghost" size="sm" disabled={!hasFilters}
                  className="h-9 rounded-md text-slate-600 hover:text-globus-navy hover:bg-slate-100 transition-colors duration-200">
                  <RotateCcw className="mr-2 h-4 w-4" /> Reset
                </Button>
              </div>
            </div>
          </div>
          {!leads ? (
            <div className="flex items-center justify-center gap-2 p-16 text-slate-500">
              <Loader2 className="h-5 w-5 animate-spin" /> Loading leads...
            </div>
          ) : leads.length === 0 ? (
            <p className="p-16 text-center text-sm text-slate-500" data-testid="no-leads-message">No inquiries yet. New submissions will appear here.</p>
          ) : (
            <div className="overflow-x-auto">
              <Table data-testid="leads-table">
                <TableHeader>
                  <TableRow>
                    <TableHead>Date</TableHead>
                    <TableHead>Name</TableHead>
                    <TableHead>Company / City</TableHead>
                    <TableHead>Contact</TableHead>
                    <TableHead>Funding</TableHead>
                    <TableHead>Facility</TableHead>
                    <TableHead>Score</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {leads.map((lead) => (
                    <TableRow key={lead.id} data-testid={`lead-row-${lead.id}`}>
                      <TableCell className="whitespace-nowrap text-xs text-slate-500">
                        {new Date(lead.created_at).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" })}
                      </TableCell>
                      <TableCell className="font-medium text-globus-navy">{lead.full_name}</TableCell>
                      <TableCell className="text-sm text-slate-600">{lead.company_name}<br /><span className="text-xs text-slate-400">{lead.city}</span></TableCell>
                      <TableCell className="text-xs text-slate-600">{lead.mobile}<br />{lead.email}</TableCell>
                      <TableCell className="whitespace-nowrap text-sm">{lead.funding_amount}</TableCell>
                      <TableCell className="whitespace-nowrap text-sm">{lead.facility}</TableCell>
                      <TableCell>
                        <span data-testid={`score-badge-${lead.id}`} title={`Readiness score: ${lead.score}/97`}
                          className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-semibold whitespace-nowrap ${GRADE_STYLES[lead.score_grade] || "bg-slate-100 text-slate-500"}`}>
                          {lead.score} · {lead.score_grade}
                        </span>
                      </TableCell>
                      <TableCell>
                        <Select value={lead.status} onValueChange={(v) => updateStatus(lead.id, v)}>
                          <SelectTrigger data-testid={`status-select-${lead.id}`} className={`h-8 w-32 rounded-md text-xs font-medium ${STATUS_COLORS[lead.status] || ""}`}>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {LEAD_STATUSES.map((s) => <SelectItem key={s} value={s} data-testid={`status-option-${s}`}>{s}</SelectItem>)}
                          </SelectContent>
                        </Select>
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-1">
                          <Button data-testid={`view-lead-${lead.id}`} variant="ghost" size="sm" onClick={() => setSelected(lead)}
                            className="h-8 w-8 p-0 text-slate-500 hover:text-globus-teal transition-colors duration-200" aria-label="View lead details">
                            <Eye className="h-4 w-4" />
                          </Button>
                          {lead.file && (
                            <Button data-testid={`view-file-${lead.id}`} variant="ghost" size="sm" onClick={() => openFile(lead)}
                              className="h-8 w-8 p-0 text-slate-500 hover:text-globus-teal transition-colors duration-200" aria-label="View uploaded file">
                              <FileText className="h-4 w-4" />
                            </Button>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </div>
      </main>

      <Dialog open={!!selected} onOpenChange={() => setSelected(null)}>
        <DialogContent data-testid="lead-detail-dialog" className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl text-globus-navy">{selected?.full_name}</DialogTitle>
          </DialogHeader>
          {selected && (
            <dl className="grid grid-cols-1 gap-x-6 gap-y-3 text-sm sm:grid-cols-2">
              {[
                ["Mobile", selected.mobile], ["Email", selected.email],
                ["Company", selected.company_name], ["City", selected.city],
                ["Annual Turnover", selected.turnover_range], ["Existing Borrowing", selected.borrowing_range],
                ["Funding Required", selected.funding_amount], ["Purpose", selected.purpose],
                ["Preferred Facility", selected.facility],
                ["Readiness Score", `${selected.score}/97 - ${selected.score_grade} (internal)`],
                ["Submitted", new Date(selected.created_at).toLocaleString("en-IN")],
              ].map(([k, v]) => (
                <div key={k}>
                  <dt className="text-[10px] tracking-[0.15em] uppercase font-semibold text-slate-400">{k}</dt>
                  <dd className="mt-0.5 text-globus-navy">{v}</dd>
                </div>
              ))}
              <div className="sm:col-span-2">
                <dt className="text-[10px] tracking-[0.15em] uppercase font-semibold text-slate-400">Financial Statement</dt>
                <dd className="mt-1">
                  {selected.file ? (
                    <Button data-testid="dialog-view-file-button" variant="outline" size="sm" onClick={() => openFile(selected)}
                      className="rounded-md border-globus-teal text-globus-teal hover:bg-globus-tealLight hover:text-globus-tealDark transition-colors duration-200">
                      <FileText className="mr-2 h-4 w-4" /> {selected.file.original_filename}
                    </Button>
                  ) : <span className="text-slate-500">Not uploaded</span>}
                </dd>
              </div>
            </dl>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
